import type { SearchResponse, SearchStatus } from "@/lib/types";

const RECENT_SEARCHES_KEY = "skill-sync.recent-searches";
const MAX_RECENT_SEARCHES = 12;

export interface RecentSearchRecord {
  searchId: string;
  query: string;
  queryTitle: string | null;
  status: SearchStatus | "submitting";
  createdAt: string;
  completedAt: string | null;
}

function isRecentSearchRecord(value: unknown): value is RecentSearchRecord {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.searchId === "string" &&
    typeof candidate.query === "string" &&
    (typeof candidate.queryTitle === "string" || candidate.queryTitle === null || candidate.queryTitle === undefined) &&
    typeof candidate.status === "string" &&
    typeof candidate.createdAt === "string" &&
    (typeof candidate.completedAt === "string" || candidate.completedAt === null)
  );
}

function persist(records: RecentSearchRecord[]) {
  window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(records));
}

function sortRecords(records: RecentSearchRecord[]) {
  return [...records].sort((left, right) => {
    const leftTime = Date.parse(left.completedAt ?? left.createdAt);
    const rightTime = Date.parse(right.completedAt ?? right.createdAt);
    return rightTime - leftTime;
  });
}

function normalizeRecord(record: RecentSearchRecord): RecentSearchRecord {
  return {
    ...record,
    queryTitle: record.queryTitle ?? null,
  };
}

export function loadRecentSearches(): RecentSearchRecord[] {
  try {
    const raw = window.localStorage.getItem(RECENT_SEARCHES_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    return sortRecords(parsed.filter(isRecentSearchRecord).map(normalizeRecord)).slice(0, MAX_RECENT_SEARCHES);
  } catch {
    return [];
  }
}

export function saveRecentSearches(records: RecentSearchRecord[]) {
  persist(sortRecords(records).slice(0, MAX_RECENT_SEARCHES));
}

export function upsertPendingRecentSearch(records: RecentSearchRecord[], searchId: string, query: string): RecentSearchRecord[] {
  const next = records.filter(record => record.searchId !== searchId);
  next.unshift({
    searchId,
    query,
    queryTitle: null,
    status: "submitting",
    createdAt: new Date().toISOString(),
    completedAt: null,
  });
  return sortRecords(next).slice(0, MAX_RECENT_SEARCHES);
}

export function upsertRecentSearchResponse(records: RecentSearchRecord[], response: SearchResponse): RecentSearchRecord[] {
  const next = records.filter(record => record.searchId !== response.search_id);
  next.unshift({
    searchId: response.search_id,
    query: response.query,
    queryTitle: response.query_title,
    status: response.status,
    createdAt: response.created_at,
    completedAt: response.completed_at,
  });
  return sortRecords(next).slice(0, MAX_RECENT_SEARCHES);
}

export function removeRecentSearch(records: RecentSearchRecord[], searchId: string): RecentSearchRecord[] {
  return records.filter(record => record.searchId !== searchId);
}
